import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  QUERY_SETTINGS,
  getSettings,
  saveSettings,
  type SaveSettingsPayload
} from "api/landlord/landlord";
import {
  QUERY_INTEGRATION_CREDENTIALS,
  updateIntegrationCredential
} from "api/landlord/integrations";
import { useAuthContext } from "providers/Auth";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { SyncOption } from "./integrations/pms/constants";
import { PropertyManagementSystem } from "shared/types/partner";
import { useCredentialSaveContext } from "./integrations/pms/credential-save-context";
import {
  COMMON_SETTINGS_KEYS,
  toSettingsPayload
} from "./integrations/pms/credential-forms/registry";
import { objectToSnakeCase } from "utils/snakeCase";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

// CRM-963: the excluded image-type ids round-trip through the untyped settings
// channel as either a real array (multi-PMS credential settings) or a JSON
// string (legacy partner settings). Normalize both to number[]; undefined means
// the partner never configured it (⇒ sync every type).
const tryParseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const parseImageTypeIds = (value: unknown): number[] | undefined => {
  if (value == null || value === "") return undefined;

  const raw = typeof value === "string" ? tryParseJson(value) : value;

  if (!Array.isArray(raw)) return undefined;
  return raw.map(Number).filter((id) => Number.isFinite(id));
};

export const useSettingsController = () => {
  const { partner, type, pms } = useAuthContext();
  const dictionary = useUnitsPropertiesDictionary();
  const credentialSaveContext = useCredentialSaveContext();
  const queryClient = useQueryClient();
  const { data: settingsData } = useQuery({
    queryKey: [QUERY_SETTINGS],
    queryFn: () => getSettings()
  });

  const {
    mutateAsync: legacyUpdateSettings,
    isPending: isLegacyUpdateSettingsPending
  } = useMutation({
    mutationFn: (input: {
      payload: SaveSettingsPayload;
      cleanupAssetUnits?: boolean;
    }) =>
      saveSettings(
        input.payload,
        input.cleanupAssetUnits ? { cleanupAssetUnits: true } : undefined
      ),
    onSuccess: () => {
      toast.success("Integration details have been updated successfully!");
      queryClient.invalidateQueries({ queryKey: [QUERY_SETTINGS] });
    },
    onError: (err) => {
      toast.error("Error saving integration details: " + getErrorMessage(err));
    }
  });

  const {
    mutateAsync: credentialUpdateSettings,
    isPending: isCredentialUpdateSettingsPending
  } = useMutation({
    mutationFn: (input: {
      partial: Record<string, string | boolean>;
      cleanupAssetUnits?: boolean;
    }) => {
      const ctx = credentialSaveContext!;
      const entries = Object.entries(
        objectToSnakeCase(input.partial) as Record<string, string | boolean>
      );
      const credentials = Object.fromEntries(
        entries.filter(([k]) => !COMMON_SETTINGS_KEYS.has(k))
      );
      const settings = Object.fromEntries(
        entries.filter(([k]) => COMMON_SETTINGS_KEYS.has(k))
      );
      return updateIntegrationCredential(ctx.credentialId, {
        credentials,
        settings,
        ...(input.cleanupAssetUnits ? { cleanup_asset_units: true } : {})
      });
    },
    onSuccess: () => {
      toast.success(
        credentialSaveContext?.integration === PropertyManagementSystem.Rentvine
          ? `Integration details saved. ${dictionary.Property} group changes sync in the background.`
          : "Integration details have been updated successfully!"
      );
      queryClient.invalidateQueries({
        queryKey: [QUERY_INTEGRATION_CREDENTIALS]
      });
    },
    onError: (err) => {
      toast.error("Error saving integration details: " + getErrorMessage(err));
    }
  });

  const useCredentialPath = type.isMultiPMS && credentialSaveContext != null;
  const updateSettings = async (
    partial: Record<string, string> | SaveSettingsPayload,
    options?: { cleanupAssetUnits?: boolean }
  ) => {
    if (useCredentialPath)
      return credentialUpdateSettings({
        partial: partial as Record<string, string>,
        cleanupAssetUnits: options?.cleanupAssetUnits
      });

    return legacyUpdateSettings({
      payload: partial as SaveSettingsPayload,
      cleanupAssetUnits: options?.cleanupAssetUnits
    });
  };
  const updateCredentials = async (
    partial: Record<string, string | boolean>
  ) => {
    if (useCredentialPath) return credentialUpdateSettings({ partial });

    return legacyUpdateSettings({
      payload: toSettingsPayload(partial) as SaveSettingsPayload
    });
  };
  const isUpdateSettingsPending = useCredentialPath
    ? isCredentialUpdateSettingsPending
    : isLegacyUpdateSettingsPending;

  const settingsRecord: Record<string, string> = useCredentialPath
    ? {
        ...(credentialSaveContext?.credentials as Record<string, string>),
        ...(credentialSaveContext?.settings as Record<string, string>)
      }
    : Object.fromEntries(
        (settingsData?.settings ?? []).map((s) => [s.name, s.value ?? ""])
      );

  const getSettingName = (name: string) => {
    if (useCredentialPath) return settingsRecord[name];
    return settingsData?.settings?.find((setting) => setting.name === name)
      ?.value;
  };

  const propertyManagementSystem = partner?.property_management_system?.name;
  const hubspotHubId = partner?.hubspot_hub_id;
  // Parse Google Analytics ID with backwards compatibility
  const parseGoogleAnalyticsId = (
    value: string | null | undefined
    // eslint-disable-next-line sonarjs/function-return-type -- Intentional mixed return for backwards compatibility
  ): string | string[] | undefined => {
    if (!value) return undefined;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : value;
    } catch {
      return value;
    }
  };

  const isAppfolioEverySyncEnabled =
    pms.isAppfolio &&
    (getSettingName("appfolio_sync") || SyncOption.EVERY_SYNC) ===
      SyncOption.EVERY_SYNC;

  const isRentvineEverySyncEnabled =
    pms.isRentvine &&
    (getSettingName("rentvine_sync") || SyncOption.NEVER) ===
      SyncOption.EVERY_SYNC;

  const isPropertywareEverySyncEnabled =
    pms.isPropertyware &&
    (getSettingName("propertyware_sync") || SyncOption.EVERY_SYNC) ===
      SyncOption.EVERY_SYNC;

  const isRentManagerEverySyncEnabled =
    pms.isRentManager &&
    (getSettingName("rent_manager_sync") || SyncOption.EVERY_SYNC) ===
      SyncOption.EVERY_SYNC;

  const isBuildiumEverySyncEnabled =
    propertyManagementSystem === PropertyManagementSystem.Buildium &&
    (getSettingName("buildium_sync") || SyncOption.NEVER) ===
      SyncOption.EVERY_SYNC;

  const isEverySyncEnabled =
    isAppfolioEverySyncEnabled ||
    isRentvineEverySyncEnabled ||
    isPropertywareEverySyncEnabled ||
    isRentManagerEverySyncEnabled ||
    isBuildiumEverySyncEnabled ||
    pms.isRentersWarehouse ||
    pms.isManageAmerica ||
    pms.isYardi;

  return {
    settingsData,
    settingsRecord,
    currentSettings: {
      google: {
        googleAnalyticsId: parseGoogleAnalyticsId(
          getSettingName("google_analytics_id")
        )
      },
      googleAds: {
        conversionId: getSettingName("google_ads_conversion_id"),
        contactFormConversionLabel: getSettingName(
          "google_ads_contact_form_conversion_label"
        ),
        showingConversionLabel: getSettingName(
          "google_ads_showing_conversion_label"
        )
      },
      resman: {
        apiKey: getSettingName("resman_api_key"),
        accountId: getSettingName("resman_account_id"),
        partnerId: getSettingName("resman_partner_id")
      },
      buildium: {
        clientId: getSettingName("buildium_client_id"),
        clientSecret: getSettingName("buildium_client_secret"),
        email: getSettingName("buildium_email"),
        password: getSettingName("buildium_password"),
        subdomain: getSettingName("buildium_subdomain"),
        buildiumSync: getSettingName("buildium_sync")
      },
      rentManager: {
        companyCode: getSettingName("rent_manager_company_code"),
        rentManagerSync: getSettingName("rent_manager_sync"),
        enableApplicationCharge: getSettingName(
          "rent_manager_enable_application_charge"
        ),
        applicationChargeCode: getSettingName(
          "rent_manager_application_charge_code"
        ),
        enableTemplateFeeCharges: getSettingName(
          "rent_manager_enable_template_fee_charges"
        ),
        administrationFeeChargeCode: getSettingName(
          "rent_manager_administration_fee_charge_code"
        ),
        holdingFeeChargeCode: getSettingName(
          "rent_manager_holding_fee_charge_code"
        )
      },
      rentersWarehouse: {
        rentersWarehouseApiKey: getSettingName("renters_warehouse_api_key"),
        rentersWarehouseUrl: getSettingName("renters_warehouse_url"),
        rentersWarehouseSalesforceDomain: getSettingName(
          "renters_warehouse_salesforce_domain"
        ),
        rentersWarehouseSalesforceClientId: getSettingName(
          "renters_warehouse_salesforce_client_id"
        ),
        rentersWarehouseSalesforceClientSecret: getSettingName(
          "renters_warehouse_salesforce_client_secret"
        ),
        rentersWarehouseSalesforceUsername: getSettingName(
          "renters_warehouse_salesforce_username"
        ),
        rentersWarehouseSalesforcePassword: getSettingName(
          "renters_warehouse_salesforce_password"
        )
      },
      manageAmerica: {
        manageAmericaCompanyId: getSettingName("manage_america_company_id")
      },
      rentvine: {
        rentvineAccount: getSettingName("rentvine_account"),
        rentvineApiKey: getSettingName("rentvine_api_key"),
        rentvineApiSecret: getSettingName("rentvine_api_secret"),
        rentvineSync: getSettingName("rentvine_sync"),
        rentvineUsePropertyGroups: getSettingName(
          "rentvine_use_property_groups"
        ),
        rentvineSelectedPropertyGroups: getSettingName(
          "rentvine_selected_property_groups"
        )
      },
      quext: {
        quextCustomerId: getSettingName("quext_customer_id")
      },
      yardi: {
        webServiceUrl: getSettingName("yardi_web_service_url"),
        username: getSettingName("yardi_username"),
        password: getSettingName("yardi_password"),
        serverName: getSettingName("yardi_server_name"),
        database: getSettingName("yardi_database"),
        enableApplicationCharge: getSettingName(
          "yardi_enable_application_charge"
        ),
        applicationChargeCode: getSettingName("yardi_application_charge_code"),
        applicationMemo: getSettingName("yardi_application_memo"),
        enableScreeningServicesCharge: getSettingName(
          "yardi_enable_screening_services_charge"
        ),
        screeningServicesChargeCode: getSettingName(
          "yardi_screening_services_charge_code"
        ),
        generalLedgerAccount: getSettingName("yardi_general_ledger_account"),
        enableTemplateFeeCharges: getSettingName(
          "yardi_enable_template_fee_charges"
        ),
        administrationFeeChargeCode: getSettingName(
          "yardi_administration_fee_charge_code"
        ),
        holdingFeeChargeCode: getSettingName("yardi_holding_fee_charge_code"),
        useLeaseTerm: getSettingName("yardi_use_lease_term")
      },
      rentcafe: {
        rentcafeApiToken: getSettingName("rentcafe_api_token"),
        rentcafeCompanyCode: getSettingName("rentcafe_company_code")
      },
      appfolio: {
        appfolioUsername: getSettingName("appfolio_username"),
        appfolioPassword: getSettingName("appfolio_password"),
        appfolioUrl: getSettingName("appfolio_url"),
        appfolioSync: getSettingName("appfolio_sync"),
        appfolioUseReports: getSettingName("appfolio_use_reports"),
        appfolioUsePropertyGroups: getSettingName(
          "appfolio_use_property_groups"
        ),
        appfolioSelectedPropertyGroups: getSettingName(
          "appfolio_selected_property_groups"
        ),
        appfolioEnableRently: getSettingName("appfolio_enable_rently"),
        appfolioUseSupergoodApi: getSettingName("appfolio_use_supergood_api"),
        appfolioSupergoodUsername: getSettingName(
          "appfolio_supergood_username"
        ),
        appfolioSupergoodPassword: getSettingName(
          "appfolio_supergood_password"
        ),
        appfolioSupergoodMfaPhone: getSettingName(
          "appfolio_supergood_mfa_phone"
        ),
        appfolioEnableResidentTagging: getSettingName(
          "appfolio_enable_resident_tagging"
        ),
        appfolioEnableYoutubeCustomButtonLink: getSettingName(
          "appfolio_enable_youtube_custom_button_link"
        )
      },
      entrata: {
        entrataDomain: getSettingName("entrata_domain"),
        entrataUsername: getSettingName("entrata_username"),
        entrataPassword: getSettingName("entrata_password"),
        entrataMfaEmail: getSettingName("entrata_mfa_email")
      },
      sdk: {
        secretKey: getSettingName("secret_key"),
        accessKey: getSettingName("access_key")
      },
      pms: {
        pms_prospect_creation_trigger: getSettingName(
          "pms_prospect_creation_trigger"
        ),
        pms_prospect_creation_on_approve: getSettingName(
          "pms_prospect_creation_on_approve"
        ),
        pms_prospect_creation_on_conditional_approve: getSettingName(
          "pms_prospect_creation_on_conditional_approve"
        ),
        pms_prospect_creation_on_decline: getSettingName(
          "pms_prospect_creation_on_decline"
        ),
        rent_manager_create_twa: getSettingName("rent_manager_create_twa"),
        rent_manager_push_inbox_communications: getSettingName(
          "rent_manager_push_inbox_communications"
        ),
        rent_manager_push_lead_stage_changes: getSettingName(
          "rent_manager_push_lead_stage_changes"
        ),
        rent_manager_sync_assets: getSettingName("rent_manager_sync_assets"),
        rent_manager_excluded_image_types: parseImageTypeIds(
          getSettingName("rent_manager_excluded_image_types")
        ),
        pms_import_inventory: getSettingName("pms_import_inventory"),
        appfolio_create_guest_cards: getSettingName(
          "appfolio_create_guest_cards"
        ),
        manage_america_pet_data_source: getSettingName(
          "manage_america_pet_data_source"
        ) as "rental_application" | "animal_screening" | undefined
      },
      revela: {
        revelaSubdomain: getSettingName("revela_subdomain")
      },
      propertyware: {
        propertywareClientId: getSettingName("propertyware_client_id"),
        propertywareClientSecret: getSettingName("propertyware_client_secret"),
        propertywareSystemId: getSettingName("propertyware_system_id"),
        propertywareMarketId:
          getSettingName("market_id") ||
          getSettingName("propertyware_market_id"),
        propertywareSync: getSettingName("propertyware_sync"),
        propertywareEmail: getSettingName("propertyware_email"),
        propertywarePassword: getSettingName("propertyware_password"),
        propertywareMfaPhone: getSettingName("propertyware_mfa_phone"),
        propertywareUsePropertyGroups: getSettingName(
          "propertyware_use_property_groups"
        )
      }
    },
    isAppfolio: pms.isAppfolio,
    isAppfolioEverySyncEnabled,
    isRentersWarehouse: pms.isRentersWarehouse,
    isManageAmerica: pms.isManageAmerica,
    hubspotHubId,
    isEverySyncEnabled,
    propertyManagementSystem,
    isCredentialPath: useCredentialPath,
    updateSettings,
    updateCredentials,
    isUpdateSettingsPending
  };
};

export const getAvailableSettings = (
  propertyManagementSystem?: PropertyManagementSystem | ""
) => {
  const decisionSettings = [
    "pms_prospect_creation_on_approve",
    "pms_prospect_creation_on_conditional_approve",
    "pms_prospect_creation_on_decline"
  ];

  const submissionOnlySettings = ["pms_prospect_creation_trigger"];

  const allSettings = [...submissionOnlySettings, ...decisionSettings];

  switch (propertyManagementSystem) {
    case PropertyManagementSystem.RentManager:
      return [
        ...allSettings,
        "rent_manager_create_twa",
        "rent_manager_push_inbox_communications",
        "rent_manager_push_lead_stage_changes",
        "rent_manager_sync_assets"
      ];
    case PropertyManagementSystem.RentersWarehouse:
      return submissionOnlySettings;
    case PropertyManagementSystem.ManageAmerica:
      return [...submissionOnlySettings, "manage_america_pet_data_source"];
    case PropertyManagementSystem.Appfolio:
      return [...allSettings, "appfolio_create_guest_cards"];
    case PropertyManagementSystem.Rentvine:
    case PropertyManagementSystem.Yardi:
    case PropertyManagementSystem.Buildium:
    case PropertyManagementSystem.Propertyware:
      return allSettings;
    case PropertyManagementSystem.Resman:
    case PropertyManagementSystem.Quext:
    case PropertyManagementSystem.Revela:
    case PropertyManagementSystem.Entrata:
      return [];

    default:
      return decisionSettings;
  }
};
